import useLocaleText from 'components/useLocaleText'
import { cameraCaptureText } from './literals'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Camera, CameraOff } from 'lucide-react'
import { Button, Loader } from 'components/ui'
import { useCameraCapture } from 'hooks/useCameraCapture'
import { BaseModalV3 } from '../BaseModal'
import { classNames } from '../classNames'
import styles from './styles.module.css'

export interface CameraCaptureProps {
  onCapture: (file: File) => void
  onClose: () => void
  renderModal?: (content: React.ReactNode, onClose: () => void) => React.ReactNode
}

// Mount only after the user chooses Take a photo. Uploading stays with the composer.
export const CameraCapture: React.FC<CameraCaptureProps> = ({ onCapture, onClose, renderModal }) => {
  const [status, setStatus] = useState<'starting' | 'ready' | 'saving' | 'error'>('starting')

  const t = useLocaleText(cameraCaptureText)

  const mounted = useRef(true)
  const wasActive = useRef(false)
  const capturing = useRef(false)

  const supported = !!navigator.mediaDevices?.getUserMedia

  const receivePhoto = useCallback(
    async (url: string) => {
      try {
        if (!mounted.current) return

        const blob = await fetch(url).then((response) => response.blob())

        if (!mounted.current) return

        onCapture(new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' }))
        onClose()
      } catch {
        if (mounted.current) setStatus('error')
      } finally {
        capturing.current = false
        URL.revokeObjectURL(url)
      }
    },
    [onCapture, onClose]
  )

  const { videoRef, isActive, open, close, capture } = useCameraCapture({ onCapture: receivePhoto })

  const dismiss = () => {
    mounted.current = false

    close()
    onClose()
  }

  const content = (
    <>
      <div className={classNames(styles.preview, status === 'error' && styles.isError)}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          aria-label={t.preview}
          onLoadedData={() => {
            if (isActive && !capturing.current) setStatus('ready')
          }}
          onError={() => {
            close()
            setStatus('error')
          }}
          className={classNames(styles.video, (status === 'ready' || status === 'saving') && styles.isVisible)}
        />
        {(status === 'starting' || status === 'error') && (
          <div role={status === 'error' ? 'alert' : 'status'} className={styles.status}>
            {status === 'error' ? <CameraOff size={28} /> : <Loader size="sm" h="24px" color="white" />}
            <span>{status === 'starting' ? t.opening : supported ? t.error : t.unsupported}</span>
          </div>
        )}
      </div>
      <div className={styles.actions}>
        <Button variant="secondary" borderRadius="full" onClick={dismiss}>
          {t.cancel}
        </Button>
        {status === 'error' ? (
          supported && (
            <Button
              bg="#1E1E1E"
              borderRadius="full"
              onClick={() => {
                wasActive.current = false
                setStatus('starting')

                open()
              }}
            >
              {t.retry}
            </Button>
          )
        ) : (
          <Button
            bg="#1E1E1E"
            borderRadius="full"
            startIcon={<Camera size={17} />}
            isDisabled={status !== 'ready'}
            isLoading={status === 'saving'}
            onClick={() => {
              capturing.current = true
              setStatus('saving')

              capture()
            }}
          >
            {t.capture}
          </Button>
        )}
      </div>
    </>
  )

  useEffect(() => {
    if (supported) open()
    else setStatus('error')

    return () => {
      mounted.current = false
    }
  }, [open, supported])

  useEffect(() => {
    if (isActive) wasActive.current = true
    // The shared hook closes itself when access fails, without exposing the browser error.
    else if (wasActive.current && !capturing.current) setStatus('error')
  }, [isActive])

  if (renderModal) return <>{renderModal(content, dismiss)}</>

  return (
    <BaseModalV3
      isOpen
      onClose={dismiss}
      closeOnOverlayClick
      closeOnEsc
      trapFocus
      header={
        <div className={styles.heading}>
          <h2>{t.title}</h2>
          <Button variant="icon" aria-label={t.close} onClick={dismiss}>
            ×
          </Button>
        </div>
      }
      body={content}
      footer={null}
      contentProps={{
        style: { width: 'calc(100vw - 32px)', maxWidth: '760px', borderRadius: 22 },
        'aria-label': t.title,
      }}
      bodyProps={{ style: { height: 'auto', maxHeight: '70vh' } }}
      footerProps={{ style: { display: 'none' } }}
    />
  )
}
